// SPDX-License-Identifier: GPL-3.0-only
// Text flow/door rules adapted from pinned OpenSaga gizflow.cpp,
// props/doors/doors.cpp and numath/nuvec.cpp. See SCRIPT_PARITY.md.
// These parsers never execute arbitrary code from asset strings.

function sourceLines(text) {
  if (typeof text !== 'string' || text.length > 16 * 1024 * 1024) throw new Error('Invalid runtime script text.');
  return text.split(/\r?\n/).map((line, index) => {
    let quoted = false, clean = '';
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') quoted = !quoted;
      if (!quoted && line[i] === '/' && line[i + 1] === '/') break;
      clean += line[i];
    }
    return { text: clean.trim(), line: index + 1 };
  }).filter(line => line.text && !line.text.startsWith(';'));
}
function tokenize(line) { return [...line.matchAll(/"([^"]*)"|([^\s]+)/g)].map(match => match[1] ?? match[2]); }
function levelTokens(line) {
  const tokens = []; let token = '', quoted = false;
  for (const char of line) {
    if (char === '"') { quoted = !quoted; continue; }
    if (/\s/.test(char) && !quoted) { if (token) { tokens.push(token); token = ''; } }
    else token += char;
  }
  if (token) tokens.push(token);
  return tokens;
}
function sourceInteger(value) {
  // NuFParGetInt: empty=>0, hexadecimal $/0x, otherwise NuAToI accepts a
  // leading decimal prefix (owned Jabba sock_start03a means index3).
  const word = String(value ?? '');
  if (/^\$[0-9a-f]+/i.test(word)) return parseInt(word.slice(1), 16);
  if (/^0x[0-9a-f]+/i.test(word)) return parseInt(word.slice(2), 16);
  return Number(/^[+-]?\d+/.exec(word)?.[0] ?? 0);
}
function reference(value) {
  if (typeof value !== 'string' || !value || value.length > 255 || /[\x00-\x1f]/.test(value)) throw new Error('Invalid runtime reference.');
  return value.toLowerCase();
}
function number(value, label) { const parsed = Number(value); if (!Number.isFinite(parsed)) throw new Error(`Invalid ${label}.`); return parsed; }
function index(value, min, max) { const parsed = Number(value); if (!Number.isInteger(parsed) || parsed < min || parsed > max) throw new Error('Invalid graph index.'); return parsed; }

export function parseLevelRuntimeConfig(text) {
  const doors = [], cameraSocks = [], cutscenes = [], unsupported = [];
  let door = null, sock = null;
  if (typeof text !== 'string' || text.length > 16 * 1024 * 1024) throw new Error('Invalid runtime script text.');
  // Level_TextLoad calls Text_StripComments(config, ...,1) before either
  // native parser. This preprocessing is deliberately not quote-sensitive:
  // '#'/' ;'/ '//' truncate the line, and ','/'=' become spaces everywhere.
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).map((raw, i) => ({ line: i + 1, text: raw.split(/#|;|\/\//, 1)[0].replace(/[=,]/g, ' ').trim() })).filter(line => line.text);
  for (const line of lines) {
    const [word, ...args] = levelTokens(line.text), key = word.toLowerCase();
    if (key === 'door_start') {
      if (door) { unsupported.push({ line: line.line, key, scope: 'door', reason: 'nested start ignored by original door parser' }); continue; }
      door = { name: null, spline: null, storyLevel: null, freeplayLevel: null, cameraSpline: null,
        cameraWait: 0, cameraBlendTime: 1, cameraLookAtPlayers: false, nextSock: null,
        oneWay: false, twoPlayersOnly: false, doNotUse: false, useAsStart: false, cutscene: null, vehicle: [], sourceLine: line.line };
    } else if (key === 'door_end') {
      if (!door) { unsupported.push({ line: line.line, key, scope: 'door', reason: 'unmatched end ignored by original door parser' }); continue; }
      if (!door.spline || !door.storyLevel) { unsupported.push({ line: door.sourceLine, key: 'door_start', scope: 'door', reason: 'incomplete door discarded by original parser' }); door = null; continue; }
      door.freeplayLevel ??= door.storyLevel;
      doors.push(door); door = null;
    } else if (door) {
      if (key === 'spline') door.name = door.spline = reference(args[0]);
      else if (key === 'level') door.storyLevel = reference(args[0]);
      else if (key === 'level_freeplay') door.freeplayLevel = reference(args[0]);
      else if (key === 'cam_spline') door.cameraSpline = reference(args[0]);
      else if (key === 'cam_wait') door.cameraWait = number(args[0], 'door camera wait');
      else if (key === 'cam_blend_time') door.cameraBlendTime = number(args[0], 'door camera blend');
      else if (key === 'next_sock') {
        const id = sourceInteger(args[0]);
        if (Number.isSafeInteger(id) && id >= 0 && id < 64) door.nextSock = id;
        else unsupported.push({ line: line.line, key, scope: 'door', reason: 'invalid camera index ignored by original parser' });
      }
      else if (['1_way', 'one_way'].includes(key)) door.oneWay = true;
      else if (['2_player_only', '2_players_only', 'two_player_only', 'two_players_only'].includes(key)) door.twoPlayersOnly = true;
      else if (key === 'do_not_use') door.doNotUse = true;
      else if (key === 'use_as_start') door.useAsStart = true;
      else if (key === 'cam_lookatplayers') door.cameraLookAtPlayers = true;
      else if (key === 'cut_scene' || key === 'cutscene') door.cutscene = reference(args[0]);
      else if (key === 'vehicle') door.vehicle.push(...args.map(reference));
      else unsupported.push({ line: line.line, key, scope: 'door' });
    } else if (key === 'sock_start') {
      if (sock) { unsupported.push({ line: line.line, key, scope: 'camera', reason: 'nested start ignored by original camera parser' }); continue; }
      const id = sourceInteger(args[0]);
      if (!Number.isSafeInteger(id) || id < 0 || id >= 64) { unsupported.push({ line: line.line, key, scope: 'camera', reason: 'invalid camera index ignored by original parser' }); continue; }
      sock = cameraSocks.find(existing => existing.id === id);
      if (!sock) { sock = { id, settings: [] }; cameraSocks.push(sock); }
    } else if (key === 'sock_end') {
      if (!sock) unsupported.push({ line: line.line, key, scope: 'camera', reason: 'unmatched end ignored by original camera parser' });
      sock = null;
    } else if (sock) sock.settings.push({ key, args });
    else if (key === 'cutscene') cutscenes.push(reference(args[0]));
    else unsupported.push({ line: line.line, key, scope: 'level', reason: 'directive preserved as unsupported; no runtime action inferred' });
  }
  if (door) unsupported.push({ line: door.sourceLine, key: 'door_start', scope: 'door', reason: 'unterminated door discarded by original parser' });
  if (sock) unsupported.push({ line: lines.at(-1)?.line, key: 'sock_start', scope: 'camera', reason: 'end of file retains original camera settings' });
  return { doors, cameraSocks, cutscenes, objectives: [], unsupported };
}

const vec = value => {
  if (Array.isArray(value) || ArrayBuffer.isView(value)) value = { x: value[0], y: value[1], z: value[2] };
  if (!value || !['x', 'y', 'z'].every(axis => Number.isFinite(value[axis]))) throw new Error('Invalid door coordinate.');
  return { x: value.x, y: value.y, z: value.z };
};
const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

export function resolveDoorGeometry(door, splinePoints) {
  if (!door?.spline || !Array.isArray(splinePoints) || splinePoints.length < 4) throw new Error('Original door spline needs at least four points.');
  const points = splinePoints.map(vec), [p0, p1, , p3] = points;
  // NuVecSurfaceNormal crosses (p3-p0) with (p1-p0), opposite the usual
  // triangle convention. Its sign determines the original one-way direction.
  const normal = cross(sub(p3, p0), sub(p1, p0)), magnitude = Math.hypot(normal.x, normal.y, normal.z);
  if (!(magnitude > 0)) throw new Error('Degenerate original door spline.');
  normal.x /= magnitude; normal.y /= magnitude; normal.z /= magnitude;
  const center = { x: 0, y: 0, z: 0 };
  for (const point of points.slice(0, 4)) for (const axis of ['x', 'y', 'z']) center[axis] += point[axis] / 4;
  const opposite = { x: p1.x + p3.x - p0.x, y: p1.y + p3.y - p0.y, z: p1.z + p3.z - p0.z };
  const radius = Math.max(...points.slice(0, 4).map(point => Math.hypot(...Object.values(sub(point, center))))) * 1.01;
  return { door, points, center, normal, opposite, radius };
}

function inTriangle(point, a, b, c) {
  const v0 = sub(b, a), v1 = sub(c, a), v2 = sub(point, a);
  const d00 = dot(v0, v0), d01 = dot(v0, v1), d11 = dot(v1, v1), d20 = dot(v2, v0), d21 = dot(v2, v1);
  const denominator = d00 * d11 - d01 * d01;
  if (!(denominator > 0)) return false;
  const v = (d11 * d20 - d01 * d21) / denominator, w = (d00 * d21 - d01 * d20) / denominator;
  return v >= 0 && w >= 0 && v + w <= 1;
}

// Previous/current positions must be native-coordinate collision centers.
// This returns a geometric event, never chapter-completion credit. The caller
// must enforce world/mission/actor conditions and run the destination pipeline.
export function checkDoorCrossing(geometry, previousCenter, currentCenter, { story = true, disabled = false, playerActive = true, ignoreDoors = false, missionActive = false, currentLevel = null } = {}) {
  if (!geometry?.door) throw new Error('Missing resolved door geometry.');
  const door = geometry.door, previous = vec(previousCenter), current = vec(currentCenter);
  if (disabled || !playerActive || ignoreDoors || door.doNotUse || (missionActive && door.storyLevel !== currentLevel)) return null;
  const before = dot(geometry.normal, sub(previous, geometry.center)), after = dot(geometry.normal, sub(current, geometry.center));
  const direction = before < 0 && after >= 0 ? 1 : !door.oneWay && before >= 0 && after < 0 ? 2 : 0;
  if (!direction) return null;
  const interpolation = Math.abs(before) / (Math.abs(before) + Math.abs(after));
  const point = { x: previous.x + (current.x - previous.x) * interpolation,
    y: previous.y + (current.y - previous.y) * interpolation, z: previous.z + (current.z - previous.z) * interpolation };
  const [p0, p1, , p3] = geometry.points;
  if (!inTriangle(point, p0, p1, p3) && !inTriangle(point, p1, geometry.opposite, p3)) return null;
  return { kind: 'door-crossing', level: story ? door.storyLevel : door.freeplayLevel, exitName: door.name,
    direction, point, nextSock: door.nextSock, cameraSpline: door.cameraSpline,
    cameraWait: door.cameraWait, cameraBlendTime: door.cameraBlendTime, cameraLookAtPlayers: door.cameraLookAtPlayers,
    cutscene: door.cutscene, twoPlayersOnly: door.twoPlayersOnly, vehicle: [...door.vehicle] };
}

export function entryFromDoor(door, splinePoints, playerIndex = 0, fallback = null) {
  index(playerIndex, 0, 7);
  if (!door?.spline || !Array.isArray(splinePoints) || splinePoints.length < 4) throw new Error('Invalid original entry door.');
  const points = splinePoints.map(vec);
  for (let player = playerIndex; player >= 0; player--) {
    const positionIndex = 4 + player * 2;
    if (points.length > positionIndex + 1) {
      const position = points[positionIndex], direction = sub(points[positionIndex + 1], position);
      return { position, heading: Math.atan2(direction.x, direction.z), playerIndex, sourcePlayerIndex: player, door: door.name };
    }
  }
  // Native source uses saved OldPlrSPos fallback if a spline lacks point 4/5.
  // Do not invent an origin or a random visible mesh as the start location.
  if (!fallback) return null;
  if (!Number.isFinite(fallback.heading)) throw new Error('Invalid entry fallback heading.');
  return { position: vec(fallback.position), heading: fallback.heading, playerIndex, sourcePlayerIndex: null, door: door.name };
}

// Players_InitPositions (players.cpp:245-278): a regular fresh level uses
// consecutive position/facing pairs from the original named "start" spline,
// cycling back to the first pair for extra players. This is distinct from
// StartDoorPositions, whose first entry pair begins at point 4.
export function startFromSpline(splinePoints, playerIndex = 0) {
  index(playerIndex, 0, 7);
  if (!Array.isArray(splinePoints)) throw new Error('Invalid original start spline.');
  if (splinePoints.length < 2) return null;
  const points = splinePoints.map(vec), pair = playerIndex % Math.floor(points.length / 2);
  const position = points[pair * 2], direction = sub(points[pair * 2 + 1], position);
  return { position, heading: Math.atan2(direction.x, direction.z), playerIndex, sourcePair: pair, source: 'original-start-spline' };
}

function braceTree(text) {
  const root = { kind: 'root', entries: [], children: [] }, stack = [root];
  const records = sourceLines(text);
  for (let recordIndex = 0; recordIndex < records.length; recordIndex++) {
    const line = { ...records[recordIndex] };
    while (/(?:^|\s)\\$/.test(line.text)) {
      if (++recordIndex >= records.length) throw new Error('Unterminated flow action continuation.');
      line.text = line.text.slice(0, -1).trimEnd() + ' ' + records[recordIndex].text;
    }
    if (line.text === '}') { if (stack.length === 1) throw new Error('Unmatched flow brace.'); stack.pop(); continue; }
    if (line.text.endsWith('{')) {
      if (stack.length > 8) throw new Error('Flow nesting exceeds supported depth.');
      const [kind, ...args] = tokenize(line.text.slice(0, -1).trim());
      const node = { kind: kind.toLowerCase(), args, entries: [], children: [], sourceLine: line.line };
      stack.at(-1).children.push(node); stack.push(node);
    } else {
      const [key, ...args] = tokenize(line.text);
      stack.at(-1).entries.push({ key: key.toLowerCase(), args, sourceLine: line.line });
    }
  }
  if (stack.length !== 1) throw new Error('Unterminated flow block.');
  return root;
}
const entry = (node, key) => node.entries.find(record => record.key === key)?.args;
const entries = (node, key) => node.entries.filter(record => record.key === key).map(record => record.args);

export function parseFlowGraph(text) {
  const tree = braceTree(text), rawBoxes = tree.children.filter(node => node.kind === 'flowbox');
  const rawCollapses = tree.children.filter(node => node.kind === 'collapse');
  if (rawBoxes.length > 512 || rawCollapses.length > 96) throw new Error('Flow graph exceeds original progress/remap capacity.');
  const collapses = rawCollapses.map((node, i) => {
    const declared = index(entry(node, 'boxid')?.[0], -96, -1);
    if (declared !== ~i) throw new Error('Collapse IDs must match original file order.');
    return { id: declared, name: entry(node, 'name')?.[0] ?? '', parents: entries(node, 'parent').map(args => ({ box: index(args[0], -96, 511), output: index(args[1], 0, 255) })), children: entries(node, 'child').map(args => index(args[0], -96, 511)) };
  });
  function flattenParent(parent, trail = []) {
    if (parent.box >= 0) return [parent];
    if (trail.includes(parent.box) || trail.length >= 96 || !collapses[~parent.box]) throw new Error('Invalid cyclic collapse parent.');
    return collapses[~parent.box].parents.flatMap(next => flattenParent(next, [...trail, parent.box]));
  }
  function flattenChild(child, trail = []) {
    if (child >= 0) return [child];
    if (trail.includes(child) || trail.length >= 96 || !collapses[~child]) throw new Error('Invalid cyclic collapse child.');
    return collapses[~child].children.flatMap(next => flattenChild(next, [...trail, child]));
  }
  const boxes = rawBoxes.map((node, i) => {
    if (index(entry(node, 'boxid')?.[0], 0, 511) !== i) throw new Error('FlowBox IDs must match original file order.');
    const parents = entries(node, 'parent').flatMap(args => flattenParent({ box: index(args[0], -96, 511), output: index(args[1], 0, 255) }));
    const children = entries(node, 'child').flatMap(args => flattenChild(index(args[0], -96, 511)));
    if (parents.length > 16 || children.length > 32) throw new Error('Flow edges exceed original capacity.');
    const bodies = node.children.filter(child => ['gizmo', 'condition', 'action'].includes(child.kind));
    const kind = bodies[0]?.kind;
    if (!kind || bodies.some(body => body.kind !== kind)) throw new Error('Invalid mixed flow-box body.');
    const box = { id: i, name: entry(node, 'name')?.[0] ?? '', kind, parents, children, sourceLine: node.sourceLine,
      gizmos: [], actions: [], condition: null, flags: { startInvisible: false, finishedInvisible: false, finishedDeactive: false, reverse: false, reverseInvisible: false, outputOnly: false, story: true, freeplay: true } };
    if (kind === 'gizmo') for (const body of bodies) {
      const type = reference(entry(body, 'type')?.[0]), name = reference(entry(body, 'name')?.[0]);
      const flags = { ...box.flags, startInvisible: false, finishedInvisible: false, finishedDeactive: false, reverse: false, reverseInvisible: false, outputOnly: false, story: true, freeplay: true };
      for (const record of body.entries) {
        if (record.key === 'startinvisible') flags.startInvisible = true;
        else if (record.key === 'finishedinvisible') flags.finishedInvisible = true;
        else if (record.key === 'finisheddeactive') flags.finishedDeactive = true;
        else if (record.key === 'reverse') flags.reverse = true;
        else if (record.key === 'revinvis') flags.reverseInvisible = true;
        else if (record.key === 'notstorymode') flags.story = false;
        else if (record.key === 'notfreeplay') flags.freeplay = false;
        else if (record.key === 'outputonly') Object.assign(flags, { startInvisible: false, finishedInvisible: false, finishedDeactive: false, reverse: false, reverseInvisible: false, outputOnly: true, story: true, freeplay: true });
      }
      box.flags = flags; // Native flags belong to the box; last Gizmo writes them.
      box.gizmos.push({ type, name, timer: entry(body, 'timer') ? number(entry(body, 'timer')[0], 'flow timer') : null,
        randomTime: !!entry(body, 'randomtime'), parameters: body.entries.map(record => ({ key: record.key, args: [...record.args] })) });
    }
    else if (kind === 'condition') {
      const type = reference(entry(bodies[0], 'type')?.[0]), allowed = ['all', 'any', 'none', 'sum', 'exactly', 'loop'];
      if (!allowed.includes(type)) throw new Error(`Unsupported original condition type: ${type}`);
      box.condition = { type, count: ['sum', 'exactly'].includes(type) ? index(entry(bodies[0], 'type')[1], 0, 255) : null,
        monitorInputs: !!entry(bodies[0], 'monitorinputs') };
    } else box.actions = bodies[0].entries.map(record => {
      if (record.args.length > 16) throw new Error('Flow action exceeds original parameter capacity.');
      return { name: record.key, parameters: [...record.args] };
    });
    if (kind === 'gizmo' && box.gizmos.length > index(entry(node, 'num_gizmos')?.[0] ?? 1, 1, 512)) throw new Error('Gizmo references exceed declared flow capacity.');
    return box;
  });
  for (const box of boxes) if (box.parents.some(parent => parent.box >= boxes.length) || box.children.some(child => child >= boxes.length)) throw new Error('Flow edge references a missing box.');
  return { boxes, collapses, rootIds: boxes.filter(box => !box.parents.length).map(box => box.id),
    unsupported: [] };
}

// Proven fresh-reset effects for the registered pickup references only.
// Other concrete gizmo callbacks and first-tick activation are deliberately
// unresolved. This helper never hides every pickup as a blanket workaround.
export function deriveFreshPickupFlowOverrides(graph, gizmos, { freeplay = false } = {}) {
  if (!graph?.boxes || !Array.isArray(gizmos?.pickups)) throw new Error('Invalid fresh pickup flow inputs.');
  const overrides = new Map(), unresolved = [];
  const registered = new Map();
  for (const pickup of gizmos.pickups) if ((pickup.configFlags & 2) || pickup.registeredGizmo) {
    if (typeof pickup.name === 'string' && !registered.has(pickup.name.toLowerCase())) registered.set(pickup.name.toLowerCase(), pickup);
  }
  function set(pickup, changes) { overrides.set(pickup.id, { ...overrides.get(pickup.id), ...changes }); }
  function deactivate(pickup) {
    set(pickup, { enabled: false });
    // Native GizmoPickup_Activate propagates enabled/visible state across
    // nonzero activation groups, including unregistered group members.
    if (pickup.activationGroup) for (const other of gizmos.pickups) if (other.activationGroup === pickup.activationGroup) set(other, { enabled: false, visible: false });
  }
  for (const box of graph.boxes) if (box.kind === 'gizmo') for (const ref of box.gizmos) {
    if (ref.type !== 'gizmopickup') continue;
    const pickup = registered.get(ref.name.toLowerCase());
    if (!pickup) { unresolved.push({ box: box.id, type: ref.type, name: ref.name, reason: 'registered original pickup not found' }); continue; }
    if (!box.flags.outputOnly) deactivate(pickup);
    const modeAllowed = freeplay ? box.flags.freeplay : box.flags.story;
    if (!modeAllowed || (box.flags.startInvisible && box.parents.length !== 0)) {
      // GizmoSetVisibility(0) first deactivates, then hides the target.
      deactivate(pickup); set(pickup, { visible: false });
    }
  }
  return { phase: 'fresh-flow-reset-before-first-tick', overrides, unresolved, requiresRuntime: true };
}

// Source flow state machine. Bindings provide real per-type runtime
// callbacks. Missing outputs remain unknown (never false, which would make a
// None node fire). Reverse callbacks and unknown physical outputs stay blocked.
export class FlowGraphRuntime {
  constructor(graph, bindings = {}, { freeplay = false, operationBudget = 8192 } = {}) {
    if (!graph?.boxes || !Number.isInteger(operationBudget) || operationBudget < 1 || operationBudget > 100000) throw new Error('Invalid flow runtime.');
    if (!['activate', 'setVisibility', 'getOutput'].every(name => typeof bindings[name] === 'function')) throw new Error('Flow runtime requires real activation, visibility and output bindings.');
    this.graph = graph; this.bindings = bindings; this.freeplay = freeplay; this.operationBudget = operationBudget;
    this.reset();
  }
  reset() {
    this.states = this.graph.boxes.map(box => ({ active: box.parents.length === 0, complete: false, actionCursor: 0,
      reversing: false, waitingChildren: false, firstLoop: true, everProcessed: false, monitorInputs: !!box.condition?.monitorInputs, loopParentCount: 0 }));
    for (const box of this.graph.boxes) if (box.condition?.type === 'loop') {
      const visited = new Set(); let count = 0;
      const walk = id => {
        if (id === box.id) { count++; return; }
        for (const child of this.graph.boxes[id].children) if (!visited.has(child) || child === box.id) { visited.add(child); walk(child); }
      };
      for (const child of box.children) walk(child);
      this.states[box.id].loopParentCount = count;
      this.states[box.id].active = box.parents.length === count;
      this.states[box.id].everProcessed = this.states[box.id].active;
    }
    this.blocked = [];
    for (const box of this.graph.boxes) if (box.kind === 'gizmo' && !box.flags.outputOnly) {
      for (const gizmo of box.gizmos) {
        this.bindings.activate?.(gizmo, false);
        const allowed = this.freeplay ? box.flags.freeplay : box.flags.story;
        if (!allowed || (box.flags.startInvisible && !this.states[box.id].active)) this.bindings.setVisibility?.(gizmo, false);
      }
    }
  }
  #spend() { if (--this.budget < 0) throw new Error('Flow operation budget exhausted.'); }
  #condition(box, trail) {
    this.#spend();
    const values = box.parents.map(parent => this.#output(parent.box, parent.output, trail));
    const yes = values.filter(value => value === true).length, unknown = values.filter(value => value === null).length;
    if (box.condition.type === 'loop') {
      const state = this.states[box.id], required = box.parents.length - (state.firstLoop ? state.loopParentCount : 0);
      return yes >= required ? true : yes + unknown < required ? false : null;
    }
    if (box.condition.type === 'all') return values.includes(false) ? false : unknown ? null : true;
    if (box.condition.type === 'none') return yes ? false : unknown ? null : true;
    if (box.condition.type === 'any') return yes ? true : unknown ? null : false;
    if (box.condition.type === 'sum') return yes >= box.condition.count ? true : yes + unknown < box.condition.count ? false : null;
    return unknown ? (yes > box.condition.count || yes + unknown < box.condition.count ? false : null) : yes === box.condition.count;
  }
  #output(boxId, output, trail = []) {
    this.#spend();
    if (trail.includes(boxId)) return null;
    const box = this.graph.boxes[boxId], state = this.states[boxId];
    if (box.kind === 'gizmo') {
      if (!(this.freeplay ? box.flags.freeplay : box.flags.story)) return true;
      const values = box.gizmos.map(gizmo => {
        const value = this.bindings.getOutput?.(gizmo, output, box.flags.outputOnly);
        return value === true || value === 1 ? true : value === false || value === 0 ? false : null;
      });
      return values.includes(false) ? false : values.includes(null) ? null : true;
    }
    if (box.kind === 'condition' && state.monitorInputs) {
      const saved = state.firstLoop; state.firstLoop = true;
      try { return this.#condition(box, [...trail, boxId]); } finally { state.firstLoop = saved; }
    }
    return state.complete;
  }
  #finish(box, trail = new Set()) {
    this.#spend();
    if (trail.has(box.id)) return;
    trail.add(box.id);
    for (const ref of box.parents) {
      const parent = this.graph.boxes[ref.box], state = this.states[ref.box];
      if (!state.waitingChildren) continue;
      if (!parent.children.every(id => {
        const child = this.graph.boxes[id], status = this.states[id];
        return child.flags.reverse || child.flags.reverseInvisible ? status.complete && !status.reversing : !status.active || id === box.id;
      })) continue;
      if (parent.flags.finishedInvisible) parent.gizmos.forEach(g => this.bindings.setVisibility(g, false));
      else if (parent.flags.finishedDeactive) parent.gizmos.forEach(g => this.bindings.activate(g, false));
      if (parent.flags.reverse) { state.reversing = false; this.#finish(parent, trail); }
      state.waitingChildren = false;
    }
  }
  #resetLoop(box) {
    // Native gives each direct child a fresh traversal checksum.
    for (const first of box.children) {
      const visited = new Set([box.id]);
      const walk = id => {
        this.#spend(); if (visited.has(id)) return; visited.add(id);
        const child = this.graph.boxes[id];
        if (child.kind === 'gizmo' && !child.flags.outputOnly) child.gizmos.forEach(g => this.bindings.activate(g, false));
        child.children.forEach(walk);
      };
      walk(first);
    }
  }
  tick() {
    this.budget = this.operationBudget; this.blocked = [];
    const queue = this.states.flatMap((state, i) => state.active || state.reversing ? [{ id: i, fromCondition: false }] : []), processed = new Set();
    while (queue.length) {
      this.#spend();
      const queued = queue.shift(), boxId = queued.id;
      // Original ProcessFlowBox explicitly permits a condition to revisit a
      // child in the same frame, even if another parent already processed it.
      if (processed.has(boxId) && !queued.fromCondition) continue;
      processed.add(boxId);
      const box = this.graph.boxes[boxId], state = this.states[boxId];
      const unsupported = this.graph.unsupported.find(item => item.id === boxId);
      if (unsupported) { this.blocked.push(unsupported); continue; }
      if (state.reversing) {
        const values = box.parents.map(parent => this.#output(parent.box, box.parents[0].output));
        if (values.includes(null) && !values.includes(false)) this.blocked.push({ id: boxId, reason: 'unresolved reverse input' });
        else if (typeof this.bindings.activateReverse !== 'function') this.blocked.push({ id: boxId, reason: 'unbound reverse activation' });
        else box.gizmos.forEach(g => this.bindings.activateReverse(g, values.includes(false), box.flags.reverseInvisible));
      }
      if (!state.active) continue;
      let ready = box.kind === 'condition' ? this.#condition(box, [boxId]) : true;
      if (box.kind !== 'condition') for (const parent of box.parents) {
        const output = this.#output(parent.box, parent.output);
        if (output === false) { ready = false; break; }
        if (output === null) ready = null;
      }
      if (ready !== true) { if (ready === null) this.blocked.push({ id: boxId, reason: 'unbound or unresolved input' }); continue; }
      if (box.condition?.type === 'loop') this.#resetLoop(box);
      if (box.kind === 'gizmo') this.#finish(box);
      if (box.kind === 'gizmo' && !box.flags.outputOnly && (this.freeplay ? box.flags.freeplay : box.flags.story)) {
        if (typeof this.bindings.activate !== 'function') { this.blocked.push({ id: boxId, reason: 'unbound activation' }); continue; }
        box.gizmos.forEach(gizmo => this.bindings.activate(gizmo, true));
      } else if (box.kind === 'action') {
        let blocked = false;
        while (state.actionCursor < box.actions.length) {
          const action = box.actions[state.actionCursor], callback = this.bindings.actions && Object.hasOwn(this.bindings.actions, action.name) ? this.bindings.actions[action.name] : null;
          if (typeof callback !== 'function' || callback(action.parameters, box) === false) { blocked = true; this.blocked.push({ id: boxId, reason: `unbound action: ${action.name}` }); break; }
          state.actionCursor++;
        }
        if (blocked) continue;
      }
      if (state.firstLoop && state.everProcessed) state.firstLoop = false;
      state.everProcessed = true; state.active = false; state.complete = true;
      if (box.kind === 'action') state.actionCursor = 0;
      if (box.kind === 'gizmo') {
        if (box.flags.finishedInvisible || box.flags.finishedDeactive) {
          state.waitingChildren = box.children.length > 0;
          if (!state.waitingChildren) box.gizmos.forEach(g => box.flags.finishedInvisible ? this.bindings.setVisibility(g, false) : this.bindings.activate(g, false));
        }
        if (box.flags.reverse || box.flags.reverseInvisible) {
          state.waitingChildren = true; state.reversing = true;
          for (const parent of box.parents) {
            if (this.graph.boxes[parent.box].kind === 'condition') this.states[parent.box].monitorInputs = true;
            if (this.graph.boxes[parent.box].kind === 'gizmo' && this.graph.boxes[parent.box].flags.reverse) {
              this.states[parent.box].reversing = true; this.states[parent.box].waitingChildren = true;
            }
          }
        }
      } else this.#finish(box);
      for (const child of box.children) { this.states[child].active = true; queue.push({ id: child, fromCondition: box.kind === 'condition' }); }
    }
    return { states: this.states.map(state => ({ ...state })), blocked: this.blocked.map(item => ({ ...item })) };
  }
}
